/**
 * PipelineHealthCard — Iter-126
 *
 * Ageing-bucket dashboard for currently PENDING quotations. Answers
 * "how much of my pipeline is going stale?" at a glance.
 *
 * Buckets: Fresh (0-7d) / Follow-up needed (8-30d) / Cold (31-90d) /
 * Stale (>90d). Backend returns count, value, %, top 3 highest-value
 * quotes per bucket. UI shows animated stacked bar + per-bucket cards
 * with drill-through.
 *
 * Thresholds are configurable via a small popover so the owner can tune
 * "what counts as fresh vs warm" for their business rhythm.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronRight,
  Flame, Snowflake, Sparkles, Settings2, Clock,
} from 'lucide-react';
import { api } from '../../lib/store';
import { logger } from '../../lib/logger';
import { toast } from 'sonner';

const short = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '₹0';
  const a = Math.abs(n);
  if (a >= 10000000) return `₹${(n / 10000000).toFixed(2)}Cr`;
  if (a >= 100000) return `₹${(n / 100000).toFixed(2)}L`;
  if (a >= 1000) return `₹${(n / 1000).toFixed(0)}K`;
  return `₹${Math.round(n)}`;
};

// Bucket visual config — status scales (emerald / amber / sky / red)
const BUCKET_STYLE = {
  fresh: { icon: Sparkles, bar: 'bg-emerald-700', soft: 'bg-emerald-50', text: 'text-emerald-700' },
  warm:  { icon: Clock, bar: 'bg-amber-500', soft: 'bg-amber-50', text: 'text-amber-700' },
  cold:  { icon: Snowflake, bar: 'bg-sky-700', soft: 'bg-sky-50', text: 'text-sky-700' },
  stale: { icon: AlertTriangle, bar: 'bg-red-700', soft: 'bg-red-50', text: 'text-red-700' },
};

const FLAG_STYLE = {
  good:    { icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-700' },
  warn:    { icon: AlertTriangle, cls: 'bg-amber-50 text-amber-700' },
  bad:     { icon: AlertTriangle, cls: 'bg-red-50 text-red-700' },
  neutral: { icon: Activity, cls: 'bg-heda-cream text-heda-mute' },
};

// -------- count-up number ----------
function CountUp({ value, format = (n) => Math.round(n).toString(), duration = 0.7 }) {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0, to = Number(value) || 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / (duration * 1000));
      setDisplay(from + (to - from) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value, duration]);
  return <span>{format(display)}</span>;
}

// -------- stacked bar segments ----------
function StackedBar({ buckets, totalValue }) {
  if (!totalValue || totalValue <= 0) {
    return (
      <div className="h-3 w-full rounded-full bg-heda-cream" />
    );
  }
  const segments = buckets.filter(b => b.value > 0);
  return (
    <div className="w-full h-3 rounded-full bg-heda-cream overflow-hidden flex" data-testid="pipeline-stacked-bar">
      {segments.map((b, i) => {
        const style = BUCKET_STYLE[b.key] || BUCKET_STYLE.warm;
        return (
          <motion.div
            key={b.key}
            initial={{ width: 0 }}
            animate={{ width: `${b.pct}%` }}
            transition={{ duration: 0.9, delay: i * 0.12, ease: 'easeOut' }}
            className={`h-full ${style.bar}`}
            title={`${b.label}: ${b.pct}%`}
          />
        );
      })}
    </div>
  );
}

// -------- per-bucket tile ----------
function BucketTile({ bucket, onDrill }) {
  const style = BUCKET_STYLE[bucket.key] || BUCKET_STYLE.warm;
  const Icon = style.icon;
  const rangeLabel =
    bucket.age_to === null
      ? `> ${bucket.age_from - 1}d old`
      : bucket.age_from === 0
      ? `0–${bucket.age_to}d old`
      : `${bucket.age_from}–${bucket.age_to}d old`;
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      whileHover={{ y: -2 }}
      className="rounded-xl border border-heda-line bg-white transition-colors hover:border-heda-gold"
      data-testid={`pipeline-bucket-${bucket.key}`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1.5">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full ${style.soft}`}>
              <Icon className={`w-3.5 h-3.5 ${style.text}`} />
            </span>
            <span className={`text-[10.5px] uppercase tracking-wider font-semibold ${style.text}`}>{bucket.label}</span>
          </div>
          <span className="text-[11px] text-heda-mute tabular-nums">{rangeLabel}</span>
        </div>
        <p className="font-display text-2xl font-bold text-heda-ink tabular-nums leading-tight">
          <CountUp value={bucket.value} format={short} />
        </p>
        <div className="flex items-center justify-between mt-1">
          <p className="text-[11px] text-heda-sub">
            <b className="tabular-nums">{bucket.count}</b> quote{bucket.count === 1 ? '' : 's'}
          </p>
          <span className="text-[10px] font-bold text-heda-mute bg-heda-cream px-2 py-0.5 rounded-full tabular-nums">
            {bucket.pct}%
          </span>
        </div>
        {bucket.top.length > 0 && (
          <div className="mt-2 pt-2 border-t border-heda-line space-y-1">
            {bucket.top.slice(0, 3).map((q) => (
              <button
                key={q.id}
                onClick={() => onDrill(q)}
                className="w-full flex items-center justify-between gap-1 text-left hover:bg-heda-cream/60 px-1.5 py-1 rounded-xl transition-colors group"
                data-testid={`pipeline-top-quote-${q.id}`}
              >
                <span className="text-[11px] text-heda-ink truncate flex-1">{q.customer}</span>
                <span className={`text-[10.5px] tabular-nums font-semibold ${style.text}`}>{short(q.value)}</span>
                <ChevronRight className="w-3 h-3 text-heda-mute group-hover:text-heda-gold flex-shrink-0" />
              </button>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// -------- threshold-tuning popover ----------
function ThresholdEditor({ thresholds, onSave, onClose }) {
  const [t, setT] = useState(thresholds);
  const save = () => {
    if (t.fresh_days < t.warm_days && t.warm_days < t.cold_days) {
      onSave(t);
    } else {
      toast.error('Fresh days must be less than Warm days, and Warm less than Cold.');
    }
  };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
      className="fixed inset-x-4 top-24 z-40 rounded-2xl border border-heda-line bg-white shadow-heda p-4 sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-1 sm:w-72"
      data-testid="pipeline-threshold-editor"
    >
      <p className="text-[13px] font-semibold mb-2 text-heda-ink">Ageing buckets — set your own thresholds</p>
      <div className="grid grid-cols-3 gap-2">
        {[
          { k: 'fresh_days', label: 'Fresh (≤)' },
          { k: 'warm_days', label: 'Warm (≤)' },
          { k: 'cold_days', label: 'Cold (≤)' },
        ].map(({ k, label }) => (
          <div key={k}>
            <Label className="heda-label !text-[10.5px]">{label}</Label>
            <Input
              type="number" min={1} max={365}
              value={t[k]}
              onChange={(e) => setT({ ...t, [k]: Number(e.target.value) || 0 })}
              className="h-10 rounded-xl bg-heda-cream/50 text-base sm:text-sm"
              data-testid={`pipeline-threshold-${k}`}
            />
          </div>
        ))}
      </div>
      <p className="text-[11px] text-heda-mute mt-1.5">Anything older than the Cold threshold is flagged as “Stale” 🔴</p>
      <div className="flex gap-2 mt-2 justify-end">
        <button type="button" onClick={onClose} className="heda-pill">Cancel</button>
        <button type="button" onClick={save} className="heda-btn !py-1.5 !px-3 !text-[12.5px]" data-testid="pipeline-threshold-save">Apply</button>
      </div>
    </motion.div>
  );
}

// ============ Main card ============
export default function PipelineHealthCard() {
  const navigate = useNavigate();
  const [thresholds, setThresholds] = useState({ fresh_days: 7, warm_days: 30, cold_days: 90 });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError('');
    api.getPipelineHealth(thresholds)
      .then(r => setData(r.data))
      .catch(e => {
        logger.warn('pipeline-health fetch failed', e);
        setError(e.response?.data?.detail || 'Failed to load pipeline');
      })
      .finally(() => setLoading(false));
  }, [thresholds]);

  const flagStyle = FLAG_STYLE[data?.flag || 'neutral'];
  const FlagIcon = flagStyle.icon;

  const drillTo = (q) => {
    // Owner clicks a top-value pending quote → jump to quotation detail
    navigate(`/quotations/${q.id}`);
  };

  if (loading) {
    return (
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="pipeline-health-loading">
        <CardContent className="p-5">
          <div className="h-6 w-48 mb-3 rounded-full shimmer-bg animate-shimmer" />
          <div className="h-3 w-full mb-3 rounded-full shimmer-bg animate-shimmer" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[1, 2, 3, 4].map(i => <div key={i} className="h-32 rounded-xl shimmer-bg animate-shimmer" />)}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="rounded-2xl border-heda-line bg-white shadow-none" data-testid="pipeline-health-error">
        <CardContent className="p-5">
          <p className="heda-error">{error || 'No pipeline data'}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-2xl border-heda-line bg-white shadow-none overflow-visible" data-testid="pipeline-health-card">
      <CardHeader className="border-b border-heda-line px-5 py-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <CardTitle className="flex items-center gap-2 font-display text-[15px] font-bold text-heda-ink">
              <Activity className="w-4 h-4 text-heda-gold" /> Pending Pipeline Health
            </CardTitle>
            <p className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11.5px] font-medium ${flagStyle.cls}`}>
              <FlagIcon className="w-3.5 h-3.5 shrink-0" /> {data.headline}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-[11px] uppercase tracking-wider text-heda-mute">Total pending</p>
              <p className="font-display text-lg font-bold tabular-nums leading-tight text-heda-ink">
                <CountUp value={data.total.value} format={short} /> · <span className="text-xs font-medium text-heda-mute">{data.total.count} quotes</span>
              </p>
            </div>
            <div className="relative">
              <button
                type="button"
                onClick={() => setEditOpen(o => !o)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-heda-line bg-white text-heda-ink transition hover:border-heda-gold hover:text-heda-gold"
                title="Tune bucket thresholds"
                data-testid="pipeline-threshold-toggle"
              >
                <Settings2 className="w-4 h-4" />
              </button>
              <AnimatePresence>
                {editOpen && (
                  <ThresholdEditor
                    thresholds={thresholds}
                    onSave={(t) => { setThresholds(t); setEditOpen(false); }}
                    onClose={() => setEditOpen(false)}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-5 py-4 space-y-3">
        {/* Stacked bar showing value split across buckets */}
        <StackedBar buckets={data.buckets} totalValue={data.total.value} />
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] font-medium">
          {data.buckets.map(b => {
            const s = BUCKET_STYLE[b.key];
            return (
              <span key={b.key} className={`inline-flex items-center gap-1 ${s.text}`}>
                <span className={`inline-block w-2 h-2 rounded-full ${s.bar}`} />
                {b.label} · {b.pct}%
              </span>
            );
          })}
        </div>

        {/* Per-bucket tiles with top-3 drill-through */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {data.buckets.map(b => (
            <BucketTile key={b.key} bucket={b} onDrill={drillTo} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
